import type { ShowcaseBannerParams } from '@yandex-int/k-common/typings/api/models/main-page';
import { sortBy, shuffle } from 'lodash';
import { Slider, useStaticUrls, ArrowButton, ShowcaseBanner } from 'platform-components';
import { getFeatureFlags } from 'platform-components/src/components/app-factory/selectors';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { useMedia } from 'react-use';
import ym from 'react-yandex-metrika';

import { ShowcaseMetrikaParams } from 'common.components/typings';

import './banner-spinner.scss';

interface Props {
    banners: ShowcaseBannerParams[];
    metrikaParams?: ShowcaseMetrikaParams;
}

export const BannerSpinnerWidget = (props: Props) => {
    const { banners, metrikaParams } = props;
    const { parentVitrinaBannerShuffle = false, teacherVitrinaBannerShuffle = false } =
        useSelector(getFeatureFlags) || {};
    const staticUrls = useStaticUrls();
    const [slideId, setSlideId] = useState<string | null>(null);

    const isDesktop = useMedia('(min-width: 769px)');

    const isLibraryTeamMain = Boolean(useRouteMatch(staticUrls.labLibraryTeamMain));
    const isLibraryMain = Boolean(useRouteMatch(staticUrls.labLibraryMain));
    const isTeacherPage = isLibraryTeamMain || isLibraryMain;

    const items = useMemo(() => {
        if ((isTeacherPage && teacherVitrinaBannerShuffle) || (!isTeacherPage && parentVitrinaBannerShuffle)) {
            const shuffledBanners = shuffle(banners);

            // сохраняем новый порядок, чтобы отправить его в метрике и не мучаться с его определением в <Slider />
            return shuffledBanners.map((banner, index) => ({ ...banner, order: index + 1 }));
        }

        return sortBy(banners, ({ order }) => order);
    }, [banners, teacherVitrinaBannerShuffle, parentVitrinaBannerShuffle, isTeacherPage]);

    const handleBannerClick = (bannerId: string) => {
        ym('reachGoal', isTeacherPage ? 'teacher_main_promo_click' : 'parent_main_promo_click', {
            ...metrikaParams,
            bannerId,
        });
    };

    const handleSlideChange = (params: { id: string; order: number; inViewport: boolean }) => {
        const { id, order, inViewport } = params;
        if (inViewport && id && slideId !== id) {
            ym('reachGoal', isTeacherPage ? 'teacher_main_promo_view' : 'parent_main_promo_view', {
                bannerId: id,
                perceived_order: order,
            });
            setSlideId(id);
        }
    };

    return (
        <Slider
            autoSpinTimeout={6000}
            hideInactiveItems
            infinite
            mix="banner-spinner"
            nextButton={<ArrowButton direction={ArrowButton.DIRECTION.RIGHT} />}
            onSlideChange={handleSlideChange}
            prevButton={<ArrowButton direction={ArrowButton.DIRECTION.LEFT} />}
            shouldShowControls={isDesktop}
            shouldShowDots={!isDesktop}
        >
            {items.map((bannerParams) => (
                <ShowcaseBanner
                    key={bannerParams.id}
                    onClick={() => handleBannerClick(bannerParams.id)}
                    {...bannerParams}
                />
            ))}
        </Slider>
    );
};
